import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  color: string;
}

interface ParticleEffectProps {
  active: boolean;
  x: number;
  y: number;
  color?: string;
  particleCount?: number;
}

export const ParticleEffect: FC<ParticleEffectProps> = ({
  active,
  x,
  y,
  color = '#FCD34D',
  particleCount = 12
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const animationRef = useRef<number>();

  useEffect(() => {
    if (!active) {
      return;
    }

    const particles: Particle[] = [];
    for (let i = 0; i < particleCount; i += 1) {
      const angle = (Math.PI * 2 * i) / particleCount + Math.random() * 0.6;
      const speed = 2 + Math.random() * 4;

      particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2,
        life: 1,
        maxLife: 0.6 + Math.random() * 0.6,
        size: 3 + Math.random() * 3,
        color
      });
    }

    particlesRef.current = [...particlesRef.current, ...particles];
  }, [active, x, y, color, particleCount]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return undefined;
    }

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particlesRef.current = particlesRef.current.filter((particle) => {
        const particleData = particle;
        particleData.x += particleData.vx;
        particleData.y += particleData.vy;
        particleData.vy += 0.25;
        particleData.life -= 0.02;

        if (particleData.life > 0) {
          ctx.globalAlpha = particleData.life / particleData.maxLife;
          ctx.fillStyle = particleData.color;
          ctx.beginPath();
          ctx.arc(particleData.x, particleData.y, particleData.size, 0, Math.PI * 2);
          ctx.fill();
          return true;
        }
        return false;
      });

      ctx.globalAlpha = 1;
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return <canvas ref={canvasRef} className="pointer-events-none fixed inset-0 z-40" />;
};

interface ParticleEffectState {
  id: string;
  x: number;
  y: number;
}

export const useParticles = () => {
  const [particleEffects, setParticleEffects] = useState<ParticleEffectState[]>([]);

  const triggerParticles = (x: number, y: number) => {
    const id = `${Date.now()}-${Math.random()}`;
    setParticleEffects((prev) => [...prev, { id, x, y }]);

    window.setTimeout(() => {
      setParticleEffects((prev) => prev.filter((effect) => effect.id !== id));
    }, 800);
  };

  return { particleEffects, triggerParticles };
};

